import dayjs, { Dayjs } from 'dayjs'
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { IconRegularChevronDown, IconRegularChevronLeft, IconRegularChevronRight, IconRegularChevronUp } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// types
import { DateCellData, DateCellsData, DatePickerProps, GetDateCellsDataArgs, SelectedViewDates, SelectedViewMonthGridDates } from './types'

// utils
import { DEFAULT_DATE_FORMAT } from '../../utils/enums'

// styles
import * as SC from './DatePickerStyles'

// hooks
import useMessages from '../../hooks/useMessages'

// components
import OverlayLoading from '../OverlayLoading/OverlayLoading'
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden'

const DATE_LABEL_FORMAT = 'MMMM YYYY'

const generateUniqueAnimationKey = (prefix = 'id') => {
	return `${prefix}-${Math.random().toString(36).substr(2, 9)}`
}

const getWeekDays = () => {
	const firstDayOfWeek = dayjs().localeData().firstDayOfWeek()
	const weekdays = dayjs().localeData().weekdaysMin()
	return [...weekdays.slice(firstDayOfWeek), ...weekdays.slice(0, firstDayOfWeek)]
}

const getDateCellsData = (data: GetDateCellsDataArgs): DateCellsData => {
	const { selectedViewDates, value, responseData, isWeeklyView } = data
	const { startDate, endDate } = selectedViewDates

	// Calculate the total number of days to display
	const dayCellsCount = isWeeklyView ? 7 : endDate.diff(startDate, 'day') + 1

	// Get the weekday for grid shift (only relevant for monthly view)
	const startDayOfTheWeek = startDate.weekday()

	const dateCellsData: DateCellData[] = [...Array(dayCellsCount).keys()].map((index) => {
		const date = startDate.add(index, 'day').startOf('day')
		const isAvailable = responseData ? !!responseData?.find((d) => dayjs(d.date).isSame(date, 'date'))?.available : false

		const isSelected = !!value?.isSame(date, 'date')
		const isPast = date.isBefore(dayjs(), 'date')

		return {
			isPast,
			isAvailable,
			isSelected,
			isToday: date.isSame(dayjs(), 'date'),
			date,
			dateString: date.format(DEFAULT_DATE_FORMAT),
			day: date.date()
		}
	})

	return {
		// For monthly view, shift the grid according to the first day of the month
		gridShift: isWeeklyView ? 0 : startDayOfTheWeek + 1,
		data: dateCellsData
	}
}

export const getSelectedViewDates = (value: Dayjs, isWeeklyView: boolean): SelectedViewDates => {
	const differenceUnit = isWeeklyView ? 'week' : 'month'
	const startDate = value.startOf(differenceUnit)

	return { startDate, endDate: startDate.endOf(differenceUnit) }
}

export const isValueInTheSelectedView = (value: Dayjs, { startDate, endDate }: SelectedViewDates) => {
	return value.isBetween(startDate, endDate, 'date', '[]')
}

export const getFullMonthGridDatesFromSelectedDates = (selectedViewDates: SelectedViewDates, value: dayjs.Dayjs | undefined): SelectedViewMonthGridDates => {
	const startOfTheSelectedMonth = (value || selectedViewDates.startDate).startOf('month')
	return { startDate: startOfTheSelectedMonth.startOf('week'), endDate: startOfTheSelectedMonth.endOf('month').endOf('week'), startOfTheSelectedMonth }
}

/**
 * Picks which day should own the single tab stop (roving tabindex) when the grid mounts
 * or the visible range changes: selected date → today → first non-past day → first cell.
 */
const getInitialFocusedDateString = (cells: DateCellData[], selectedValue: Dayjs | undefined): string => {
	if (selectedValue) {
		const selectedInView = cells.find((cell) => cell.date.isSame(selectedValue, 'date'))
		if (selectedInView) {
			return selectedInView.dateString
		}
	}

	const todayCell = cells.find((cell) => cell.isToday)
	if (todayCell) {
		return todayCell.dateString
	}

	const firstFocusable = cells.find((cell) => !cell.isPast)
	return firstFocusable?.dateString ?? cells[0]?.dateString ?? dayjs().format(DEFAULT_DATE_FORMAT)
}

const DatePicker = (props: DatePickerProps) => {
	const {
		value,
		onChange,
		availableDates,
		disabled,
		/**
		 * onChangeView
		 * it is highly recommended to wrap "onChangeView" function to useCallback hook for better performance, because it is used as one of dependency in useEffect dependencies array
		 */
		onChangeView,
		size = 'default',
		errorMsg,
		defaultView = 'weekly',
		allowUnselectValue = false,
		isLoading = false,
		afterChange
	} = props

	const { messages } = useMessages()
	const { formatMessage, formatDate } = useIntl()
	const monthLabelId = useId()
	const calendarErrorId = useId()

	const [isWeeklyView, setIsWeeklyView] = useState(defaultView === 'weekly')
	// start and end date of currently selected calendar view
	const [selectedViewDates, setSelectedViewDates] = useState(getSelectedViewDates(value || dayjs(), isWeeklyView))
	// start and end date of currently selected month grid
	// 1.9.2024 (first day of the month) could be Wednesday, start of the month grid in this case would be 29.8.2024 as dayjs(1.9.2024).startOf('week') => 28.9.2024
	// the same logic applies for the end date of the month grid... if 31.9.2024 (last day of the month) would be friday, end of the month grid would be 2.10.2024 as dayjs(31.9.2024).endOf('week') => 31.9.2024
	const [selectedViewMonthGridDates, setSelectedViewMonthGridDates] = useState<SelectedViewMonthGridDates>(
		getFullMonthGridDatesFromSelectedDates(selectedViewDates, value)
	)

	const [animationTransitionKey, setAnimationTransitionKey] = useState(generateUniqueAnimationKey())
	const [monthAnnouncement, setMonthAnnouncement] = useState('')

	const weekDays = getWeekDays()
	const dayCellsData = getDateCellsData({ selectedViewDates, value, responseData: availableDates, isWeeklyView })

	const [focusedDateString, setFocusedDateString] = useState(() => getInitialFocusedDateString(dayCellsData.data, value))

	const isValueChangedFromInsideComponent = useRef(false)
	const prevSelectedViews = useRef<{ prevSelectedViewDates: SelectedViewDates; prevSelectedViewMonthGridDates: SelectedViewMonthGridDates }>({
		prevSelectedViewDates: selectedViewDates,
		prevSelectedViewMonthGridDates: selectedViewMonthGridDates
	})
	const dayButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
	/** When true, the next viewAnnouncementLabel change is spoken via the polite live region (user-driven only). */
	const isUserInteractionViewChangeRef = useRef(false)

	// string value is better as use effect dependency than Dayjs object
	const valueString = value ? value.format(DEFAULT_DATE_FORMAT) : undefined

	const monthYearLabel = useMemo(() => {
		return selectedViewMonthGridDates.startOfTheSelectedMonth.format(DATE_LABEL_FORMAT)
	}, [selectedViewMonthGridDates.startOfTheSelectedMonth])

	/** Changes with week or month navigation so the polite live region can announce both. */
	const viewAnnouncementLabel = useMemo(() => {
		if (isWeeklyView) {
			return `${formatDate(selectedViewDates.startDate.toDate(), { day: 'numeric', month: 'long', year: 'numeric' })} – ${formatDate(
				selectedViewDates.endDate.toDate(),
				{
					day: 'numeric',
					month: 'long',
					year: 'numeric'
				}
			)}`
		}

		return monthYearLabel
	}, [formatDate, isWeeklyView, monthYearLabel, selectedViewDates.endDate, selectedViewDates.startDate])

	/** Focuses the day button for a given date string (roving tabindex target). */
	const focusDayButton = useCallback((dateString: string) => {
		dayButtonRefs.current.get(dateString)?.focus()
	}, [])

	useEffect(() => {
		if (isValueChangedFromInsideComponent.current) {
			isValueChangedFromInsideComponent.current = false
			return
		}

		// Programmatic value sync must not speak via the month/week live region.
		isUserInteractionViewChangeRef.current = false

		// this use effect handle change of the calendar view when the value of calendar is changed from the outside of the component
		// if new date is located in the different calendar view than is currently active, it will scroll component to the view in which new selected date will be visible
		if (valueString) {
			const valueDayJs = dayjs(valueString)

			const isSameView = isValueInTheSelectedView(valueDayJs, selectedViewDates)
			if (!isSameView) {
				const newSelectedViewDates = getSelectedViewDates(valueDayJs, isWeeklyView)
				const newSelectedViewMonthGridDates = getFullMonthGridDatesFromSelectedDates(newSelectedViewDates, valueDayJs)
				setSelectedViewDates(newSelectedViewDates)
				setSelectedViewMonthGridDates(newSelectedViewMonthGridDates)
				setAnimationTransitionKey(generateUniqueAnimationKey())
				setFocusedDateString(valueString)

				if (onChangeView) {
					onChangeView({
						isWeeklyView,
						type: 'outsideValueChange',
						selectedViewMonthGridDates: newSelectedViewMonthGridDates,
						selectedViewDates: newSelectedViewDates
					})
				}
			}
		}
	}, [valueString, selectedViewDates, isWeeklyView, onChangeView])

	// Announce week/month view changes only after user-driven view changes (not programmatic value sync).
	useEffect(() => {
		if (!isUserInteractionViewChangeRef.current) {
			return
		}
		isUserInteractionViewChangeRef.current = false
		setMonthAnnouncement(viewAnnouncementLabel)
	}, [viewAnnouncementLabel])

	const handleChangeDate = (newDate: Dayjs, disabledButton: boolean, data: DateCellData) => {
		if (disabledButton) {
			return
		}
		isValueChangedFromInsideComponent.current = true
		setFocusedDateString(data.dateString)

		let newValue: Dayjs | undefined = newDate

		if (allowUnselectValue) {
			newValue = !value || (value && !newDate.isSame(value, 'date')) ? newDate : undefined
		}

		const newSelectedViewDates = getSelectedViewDates(newValue || selectedViewDates.startDate, isWeeklyView)
		const newSelectedViewMonthGridDates = getFullMonthGridDatesFromSelectedDates(newSelectedViewDates, newValue)

		// we need to change selected view or month grid if the new value is in different view or different month grid than currently selected
		if (!selectedViewMonthGridDates.startDate.isSame(newSelectedViewMonthGridDates.startDate, 'date')) {
			isUserInteractionViewChangeRef.current = true
			setSelectedViewDates(newSelectedViewDates)
			setSelectedViewMonthGridDates(newSelectedViewMonthGridDates)

			if (onChangeView) {
				onChangeView({
					isWeeklyView,
					type: 'onInternalValueChange',
					selectedViewMonthGridDates: newSelectedViewMonthGridDates,
					selectedViewDates: newSelectedViewDates
				})
			}
		}

		onChange(newValue)

		if (afterChange) {
			afterChange(newDate, data)
		}
	}

	/**
	 * Shared entry for week/month navigation and expand/collapse.
	 * Updates the roving tabindex day for the new grid only — focus stays on the header
	 * control the user activated (prev / next / expand live outside the loading overlay).
	 */
	const applyViewChange = (
		newSelectedViewDates: SelectedViewDates,
		newSelectedViewMonthGridDates: SelectedViewMonthGridDates,
		type: 'next' | 'prev' | 'collapse' | 'expand',
		options?: { preferredFocusedDateString?: string; newIsWeeklyView?: boolean }
	) => {
		isValueChangedFromInsideComponent.current = true
		isUserInteractionViewChangeRef.current = true

		const nextIsWeeklyView = options?.newIsWeeklyView ?? isWeeklyView
		const cells = getDateCellsData({
			selectedViewDates: newSelectedViewDates,
			value,
			responseData: availableDates,
			isWeeklyView: nextIsWeeklyView
		}).data
		const preferred = options?.preferredFocusedDateString
		const preferredInView = preferred ? cells.find((cell) => cell.dateString === preferred) : undefined
		setFocusedDateString(preferredInView?.dateString ?? getInitialFocusedDateString(cells, value))

		setSelectedViewDates(newSelectedViewDates)
		setSelectedViewMonthGridDates(newSelectedViewMonthGridDates)
		setAnimationTransitionKey(generateUniqueAnimationKey())

		if (options?.newIsWeeklyView !== undefined) {
			setIsWeeklyView(options.newIsWeeklyView)
		}

		if (onChangeView) {
			onChangeView({
				isWeeklyView: nextIsWeeklyView,
				type,
				selectedViewMonthGridDates: newSelectedViewMonthGridDates,
				selectedViewDates: newSelectedViewDates
			})
		}
	}

	/** Prev/next week or month. Focus remains on the arrow button. */
	const handleChangeCalendarViewFromArrows = (direction: 'next' | 'prev') => {
		// Ignore while loading so focus stays (no disabled attr) and we don't stack view fetches.
		if (disabled || isLoading) {
			return
		}

		const selectedViewDifferenceUnit = isWeeklyView ? 'week' : 'month'
		const newSelectedViewStartDate =
			direction === 'next'
				? selectedViewDates.startDate.add(1, selectedViewDifferenceUnit)
				: selectedViewDates.startDate.subtract(1, selectedViewDifferenceUnit)

		const newSelectedViewDates = getSelectedViewDates(newSelectedViewStartDate, isWeeklyView)
		const valueForMonthGird = value && isValueInTheSelectedView(value, newSelectedViewDates) ? value : undefined
		const newSelectedViewMonthGridDates = getFullMonthGridDatesFromSelectedDates(newSelectedViewDates, valueForMonthGird)

		applyViewChange(newSelectedViewDates, newSelectedViewMonthGridDates, direction, {
			preferredFocusedDateString: focusedDateString
		})
	}

	const onChangeWeeklyView = () => {
		let newSelectedViewDates: SelectedViewDates = selectedViewDates
		let onChangeType: 'expand' | 'collapse' = 'expand'
		let newIsWeeklyView = !isWeeklyView

		if (!isWeeklyView) {
			// change to weekly view
			if (value && isValueInTheSelectedView(value, selectedViewDates)) {
				// if selected date is in the currently selected view, collapse to the week that contains selected date
				newSelectedViewDates = getSelectedViewDates(value, newIsWeeklyView)
			} else if (
				prevSelectedViews.current.prevSelectedViewMonthGridDates.startOfTheSelectedMonth.isSame(
					selectedViewMonthGridDates.startOfTheSelectedMonth,
					'date'
				)
			) {
				newSelectedViewDates = prevSelectedViews.current.prevSelectedViewDates
			} else if (selectedViewMonthGridDates.startOfTheSelectedMonth.isSameOrBefore(dayjs(), 'date')) {
				// if currently selected view is current month, collapse to the week that contains current date, because past days are disabled
				newSelectedViewDates = getSelectedViewDates(dayjs(), newIsWeeklyView)
			} else {
				// otherwise collapse to the start of the week of the currently selected month
				newSelectedViewDates = getSelectedViewDates(selectedViewMonthGridDates.startOfTheSelectedMonth, newIsWeeklyView)
			}

			onChangeType = 'collapse'
		} else {
			prevSelectedViews.current = { prevSelectedViewDates: selectedViewDates, prevSelectedViewMonthGridDates: selectedViewMonthGridDates }
			newIsWeeklyView = false
			// change to monthly view
			newSelectedViewDates = getSelectedViewDates(selectedViewMonthGridDates.startOfTheSelectedMonth, newIsWeeklyView)
			onChangeType = 'expand'
		}

		applyViewChange(newSelectedViewDates, selectedViewMonthGridDates, onChangeType, {
			preferredFocusedDateString: focusedDateString,
			newIsWeeklyView
		})
	}

	const isPrevButtonDisabled = () => {
		const differenceUnit = isWeeklyView ? 'week' : 'month'
		return selectedViewDates.startDate.startOf(differenceUnit).isSame(dayjs().startOf(differenceUnit), 'date') || !!disabled
	}

	/** Moves the roving tab stop to a day that exists in the current visible grid. */
	const focusDateInView = (dateString: string) => {
		const cellInView = dayCellsData.data.find((cell) => cell.dateString === dateString)
		if (!cellInView) {
			return
		}

		setFocusedDateString(cellInView.dateString)
		requestAnimationFrame(() => focusDayButton(cellInView.dateString))
	}

	/**
	 * Arrow-key day navigation clamped to the current week/month only.
	 * Crossing the edge does not change period (that would fetch new availability and
	 * drop focus into the loading/inert grid). Use prev/next or expand for period changes.
	 */
	const moveFocusByDays = (delta: number) => {
		const currentCell = dayCellsData.data.find((cell) => cell.dateString === focusedDateString) ?? dayCellsData.data[0]
		if (!currentCell) {
			return
		}

		const nextDate = currentCell.date.add(delta, 'day')
		const todayStart = dayjs().startOf('day')

		if (nextDate.isBefore(todayStart, 'date')) {
			const todayInView = dayCellsData.data.find((cell) => cell.isToday)
			if (todayInView) {
				focusDateInView(todayInView.dateString)
			}
			return
		}

		const nextInView = dayCellsData.data.find((cell) => cell.date.isSame(nextDate, 'date'))
		if (nextInView) {
			focusDateInView(nextInView.dateString)
		}
	}

	/**
	 * Keyboard model for the day grid (one tab stop + arrows).
	 * Home/End jump to first/last non-past day in the current view; no PageUp/PageDown period change.
	 */
	const handleGridKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (disabled) {
			return
		}

		const { key } = event

		if (key === 'ArrowLeft') {
			event.preventDefault()
			moveFocusByDays(-1)
			return
		}

		if (key === 'ArrowRight') {
			event.preventDefault()
			moveFocusByDays(1)
			return
		}

		if (key === 'ArrowUp') {
			event.preventDefault()
			moveFocusByDays(-7)
			return
		}

		if (key === 'ArrowDown') {
			event.preventDefault()
			moveFocusByDays(7)
			return
		}

		if (key === 'Home') {
			event.preventDefault()
			const firstFocusable = dayCellsData.data.find((cell) => !cell.isPast) ?? dayCellsData.data[0]
			if (firstFocusable) {
				focusDateInView(firstFocusable.dateString)
			}
			return
		}

		if (key === 'End') {
			event.preventDefault()
			const lastFocusable = [...dayCellsData.data].reverse().find((cell) => !cell.isPast) ?? dayCellsData.data[dayCellsData.data.length - 1]
			if (lastFocusable) {
				focusDateInView(lastFocusable.dateString)
			}
		}
	}

	const prevNavLabel = isWeeklyView
		? formatMessage({ id: 'Previous week', defaultMessage: 'Previous week' })
		: formatMessage({ id: 'Previous month', defaultMessage: 'Previous month' })
	const nextNavLabel = isWeeklyView
		? formatMessage({ id: 'Next week', defaultMessage: 'Next week' })
		: formatMessage({ id: 'Next month', defaultMessage: 'Next month' })

	return (
		<SC.DatePickerWrapper>
			<SC.DatePickerHeader>
				<SC.SelectedDate id={monthLabelId}>{monthYearLabel}</SC.SelectedDate>
				<SC.MonthLiveRegion role={'status'} aria-live={'polite'} aria-atomic={'true'}>
					{monthAnnouncement}
				</SC.MonthLiveRegion>
				<SC.SliderButton
					type={'button'}
					onClick={() => handleChangeCalendarViewFromArrows('prev')}
					disabled={isPrevButtonDisabled()}
					aria-label={prevNavLabel}
				>
					<IconRegularChevronLeft color={'icon.tertiary'} aria-hidden />
				</SC.SliderButton>
				<SC.SliderButton type={'button'} onClick={() => handleChangeCalendarViewFromArrows('next')} disabled={!!disabled} aria-label={nextNavLabel}>
					<IconRegularChevronRight color={'icon.tertiary'} aria-hidden />
				</SC.SliderButton>
				<SC.ExpandButton type={'button'} onClick={onChangeWeeklyView} disabled={disabled} aria-expanded={!isWeeklyView}>
					{isWeeklyView ? (
						<>
							{messages['Expand calendar']}
							<IconRegularChevronDown color={disabled ? 'icon.tertiary' : 'icon.primary'} aria-hidden />
						</>
					) : (
						<>
							{messages['Collapse calendar']}
							<IconRegularChevronUp color={disabled ? 'icon.tertiary' : 'icon.primary'} aria-hidden />
						</>
					)}
				</SC.ExpandButton>
			</SC.DatePickerHeader>
			<SC.CalendarLoadingWrap aria-busy={isLoading || undefined}>
				{isLoading ? <VisuallyHidden role={'status'}>{formatMessage({ id: 'Loading', defaultMessage: 'Loading' })}</VisuallyHidden> : null}
				<OverlayLoading isLoading={isLoading}>
					<SC.CalendarWrapper>
						<SC.CalendarGrid aria-hidden={'true'}>
							{weekDays.map((day, index) => (
								<SC.HeaderCell $size={size} key={index}>
									{day}
								</SC.HeaderCell>
							))}
						</SC.CalendarGrid>

						<SC.CalendarTransitionAnimation key={animationTransitionKey}>
							<SC.CalendarOpacityAnimation key={`${animationTransitionKey}_${isWeeklyView}`}>
								<SC.CalendarGrid
									role={'grid'}
									aria-labelledby={monthLabelId}
									aria-describedby={errorMsg ? calendarErrorId : undefined}
									onKeyDown={handleGridKeyDown}
								>
									{dayCellsData.data.map((dayCell) => {
										const { day, date, dateString, isAvailable, isSelected, isToday, isPast } = dayCell
										const isFocusedDay = dateString === focusedDateString
										const isDayDisabled = isPast || !!disabled || isLoading

										return (
											<SC.DayCellButton
												key={dateString}
												ref={(node) => {
													if (node) {
														dayButtonRefs.current.set(dateString, node)
													} else {
														dayButtonRefs.current.delete(dateString)
													}
												}}
												type={'button'}
												role={'gridcell'}
												$isAvailable={isAvailable}
												$isSelected={isSelected}
												$isToday={isToday}
												$isPast={isPast}
												$disabled={!!disabled || isLoading}
												$size={size}
												$gridShift={day === 1 ? dayCellsData.gridShift : undefined}
												tabIndex={isFocusedDay ? 0 : -1}
												aria-selected={isSelected}
												aria-disabled={isDayDisabled}
												aria-label={formatDate(date.toDate(), {
													weekday: 'long',
													day: 'numeric',
													month: 'long',
													year: 'numeric'
												})}
												onClick={() => handleChangeDate(date, isDayDisabled, dayCell)}
												onFocus={() => setFocusedDateString(dateString)}
												onKeyDown={(event) => {
													if (event.key === 'Enter' || event.key === ' ') {
														event.preventDefault()
														handleChangeDate(date, isDayDisabled, dayCell)
													}
												}}
											>
												{day}
											</SC.DayCellButton>
										)
									})}
								</SC.CalendarGrid>
							</SC.CalendarOpacityAnimation>
						</SC.CalendarTransitionAnimation>
					</SC.CalendarWrapper>
					{errorMsg && (
						<SC.ErrorMsg id={calendarErrorId} role={'alert'}>
							{errorMsg}
						</SC.ErrorMsg>
					)}
				</OverlayLoading>
			</SC.CalendarLoadingWrap>
		</SC.DatePickerWrapper>
	)
}

export default DatePicker
